import { buildDecryptContext, type DecryptContext } from './envelope.js';
import { decryptSecret, encryptAtRest, isEncrypted } from './secret.js';

/**
 * Field names treated as secret-bearing across the three install tables
 * (organization_integrations / team_integrations / agent_integrations).
 *
 * Three categories:
 *   - OAuth2 tokens (access_token, refresh_token)
 *   - API-key auth (api_key)
 *   - Webhook / HMAC-signed auth (webhook_secret, signing_secret, client_secret)
 *   - GitHub App auth (private_key — the RS256 key that signs the App JWT, CS-1441)
 *
 * Provider-specific secret names not in this list will be persisted in
 * plaintext — keep this synchronised with the route-side assertion in
 * `packages/api/src/routes/integrations.ts`.
 */
/**
 * ENG-8588: exported so an at-rest audit derives the field list from the one
 * place that defines it. The sibling lists (`SENSITIVE_CHANNEL_FIELDS`,
 * `SENSITIVE_SETTINGS_FIELDS`) already are, and this one being private meant an
 * auditor had to hand-copy it — which produced a wrong answer the first time it
 * was done (every key in the blob counted as a secret, reporting 417 plaintext
 * values where there was exactly 1). A second hand-maintained copy of a rule is
 * how a field silently stops being covered.
 */
export const SENSITIVE_INTEGRATION_FIELDS = [
  'access_token',
  'refresh_token',
  'api_key',
  'webhook_secret',
  'signing_secret',
  'client_secret',
  'private_key',
] as const;

export type SensitiveIntegrationField = (typeof SENSITIVE_INTEGRATION_FIELDS)[number];

const SENSITIVE_SET: ReadonlySet<string> = new Set(SENSITIVE_INTEGRATION_FIELDS);

/** True if `key` is one of the secret-bearing integration-credential fields. */
export function isSensitiveIntegrationField(key: string): key is SensitiveIntegrationField {
  return SENSITIVE_SET.has(key);
}

/**
 * The three install-scope tables whose `credentials` JSONB carries these fields.
 * Exported (not a second private copy) so `secret-registry.ts` derives its
 * integration entries from the ONE place that names the tables — a second
 * hand-maintained list is how a scope silently drops out of the completeness set
 * (the same reasoning that made `SENSITIVE_INTEGRATION_FIELDS` exported, ENG-8588).
 */
export const INTEGRATION_CREDENTIAL_TABLES = [
  'agent_integrations',
  'team_integrations',
  'organization_integrations',
] as const;

export type IntegrationCredentialsTable = (typeof INTEGRATION_CREDENTIAL_TABLES)[number];

/**
 * ADR-0061 slice 3 (ENG-8896): identifies which install row a secret credential
 * field lives on, so the writer/reader can bind the org+field-grained v2 AAD
 * (`org:<org>:<table>:credentials:<field>:<row>`). Supplying it is what upgrades an
 * integration-credentials write to a v2 envelope once v2 writes are armed; omit it
 * (the default until a call site is migrated) and the path stays v1 exactly as
 * before. Mirrors `SettingsRowRef` (slice 1) and `ChannelConfigRowRef` (slice 2).
 *
 *   - `orgId`: the owning `organization_id`, resolved by ONE drift-proof authority
 *     (the API-side `integration-creds-store` centralizes it): for any row with a
 *     `team_id` (agent + team scope) it is `team_id → teams.organization_id`, NOT
 *     the denormalised `organization_id` column — team→org is the immutable source
 *     that column is copied from, and the only org value the `integrations_view`
 *     reader (which nulls the physical column, ENG-8405) and a physical-table
 *     re-seal can both compute. `organization_integrations` has no `team_id`, so its
 *     `organization_id` (the org's own id) is used directly.
 *   - `table`: which of the three install tables the row is on — the AAD `<table>`
 *     differs per scope, so unlike the single-table channel-config ref this MUST
 *     carry it (mirrors `SettingsRowRef.table`).
 *   - `rowId`: the row PRIMARY KEY (`id`, a `uuid` on all three tables). It **must**
 *     be the PK: the ADR-0061 re-seal engine keys every table on its PK, so a
 *     divergent `rowId` would make a runtime-written and a re-seal-written AAD for
 *     the same row disagree → a silent `AeadAuthFailureError` at decrypt.
 */
export type IntegrationCredsRowRef = {
  orgId: string;
  table: IntegrationCredentialsTable;
  rowId: string;
};

/** The v2 AAD context for one sensitive field on a given install-credentials row. */
export function integrationCredentialsFieldContext(
  row: IntegrationCredsRowRef,
  field: SensitiveIntegrationField,
): DecryptContext {
  return buildDecryptContext(
    { table: row.table, column: 'credentials', field },
    { orgId: row.orgId, rowId: row.rowId },
  );
}

/**
 * Encrypt sensitive token fields inside an `integrations.credentials` JSONB.
 * Non-sensitive fields are left untouched. Safe to call on a credentials blob
 * that already contains encrypted values — isEncrypted() gates re-encryption.
 *
 * With a `row` AND v2 writes armed each field is sealed into a v2 (AAD-bound)
 * envelope; otherwise it writes v1 unchanged (ADR-0061 slice 3 incremental-safe
 * writer). Idempotent, so a caller can decrypt → merge → re-encrypt without
 * double-wrapping fields it did not touch.
 *
 * On a real deployment a missing key throws (fail closed, ADR-0061 Part 4) rather
 * than persisting the token as plaintext; only local dev falls back to plaintext,
 * observably (`encryptAtRest`). Matches the channel-config / settings behaviour.
 */
export function encryptIntegrationCredentials(
  credentials: Record<string, unknown>,
  row?: IntegrationCredsRowRef,
): Record<string, unknown> {
  const out = { ...credentials };
  for (const field of SENSITIVE_INTEGRATION_FIELDS) {
    const value = out[field];
    if (typeof value === 'string' && value && !isEncrypted(value)) {
      out[field] = encryptAtRest(
        value,
        row ? integrationCredentialsFieldContext(row, field) : undefined,
      );
    }
  }
  return out;
}

/**
 * Decrypt sensitive token fields inside an `integrations.credentials` JSONB.
 * Plaintext values are returned as-is (backward compatible with rows that
 * predate encryption — they migrate on next write).
 *
 * A v2 blob requires the matching `row` to rebuild its AAD; a v1 blob ignores it.
 * A migrated reader always passes `row`, so a v2 field decrypts; an un-migrated
 * reader (no `row`) still decrypts v1 exactly as before.
 *
 * Fails loudly: if a value is wrapped with the `enc:` prefix but cannot be
 * decrypted (missing key, wrong key, corruption), we throw rather than hand
 * ciphertext back to the caller. Returning ciphertext silently would bleed
 * `enc:...` blobs into `.env.integrations` files, `.tokens.json` artifacts,
 * and outbound requests to providers — all of which look healthy to the
 * calling code until auth fails with a misleading `invalid_grant`.
 *
 * **Batch/fan-out callers** iterating many rows should use
 * `tryDecryptIntegrationCredentials` so one corrupt/mis-keyed row degrades to a
 * skipped entry instead of failing the whole batch.
 */
export function decryptIntegrationCredentials(
  credentials: Record<string, unknown>,
  row?: IntegrationCredsRowRef,
): Record<string, unknown> {
  const out = { ...credentials };
  for (const field of SENSITIVE_INTEGRATION_FIELDS) {
    const value = out[field];
    if (typeof value === 'string' && value && isEncrypted(value)) {
      out[field] = decryptSecret(
        value,
        row ? integrationCredentialsFieldContext(row, field) : undefined,
      );
    }
  }
  return out;
}

/**
 * Fail-soft variant of `decryptIntegrationCredentials` for **fan-out** callers —
 * a loop over many install rows' credentials (e.g. the managed-connection health
 * cron, the host-runtime credential-delivery denormalisation). Returns the
 * decrypted credentials, or `null` if any field is undecryptable, logging the
 * typed error with `context` so one corrupt or mis-keyed row is skipped rather
 * than throwing and blanking the whole batch — the ADR-0061 Part 4 fail-loud
 * grain chosen for these paths (mirrors `tryDecryptChannelConfig`).
 *
 * It still never returns an `enc:` blob (the leak `decryptIntegrationCredentials`
 * closes): an undecryptable row becomes `null` (skip it), never a credentials
 * object carrying ciphertext. Single-row readers must use the strict
 * `decryptIntegrationCredentials`.
 */
export function tryDecryptIntegrationCredentials(
  credentials: Record<string, unknown>,
  context: { integrationId?: string; definitionId?: string; row?: IntegrationCredsRowRef } = {},
): Record<string, unknown> | null {
  // `row` (ADR-0061 slice 3) binds the v2 AAD; `integrationId`/`definitionId` are
  // the fan-out log context. A migrated fan-out caller supplies `row`; an
  // un-migrated one omits it and keeps decrypting v1 unchanged.
  const { row, ...logContext } = context;
  try {
    return decryptIntegrationCredentials(credentials, row);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[integration-credentials] undecryptable row skipped', {
      ...logContext,
      error: message,
    });
    return null;
  }
}
